import { z } from "zod";
import type { Block, Section } from "./index.js";

/** What the presenter asks the generator for. Saved with the talk (Deck.settings.generation). */
export const GenerationOptions = z
  .object({
    audience: z.enum(["beginner", "mixed", "expert"]).optional(),
    tone: z.enum(["conversational", "neutral", "formal"]).optional(),
    interaction: z.enum(["light", "balanced", "heavy"]).optional(),
    code: z.boolean().optional(),
  })
  .strict();
export type GenerationOptions = z.infer<typeof GenerationOptions>;

/**
 * Where the server puts a screenshot pin it hasn't placed yet: spread along the middle line. Pin placement only moves
 * pins still there, so one the presenter dragged stays put.
 */
export const UNPLACED_PIN_Y = 0.5;

/* ---------- the guided studio: brief, outline plan, step (Deck.settings) ---------- */

/** What the talk is for. Written in step 1, given to the model with every draft. */
export const TalkBrief = z
  .object({
    goal: z.string().max(400).optional(), // what people should leave with
    audienceNote: z.string().max(400).optional(), // who's in the room and what they already know
    minutes: z.number().int().min(5).max(240).optional(), // the slot length
  })
  .strict();
export type TalkBrief = z.infer<typeof TalkBrief>;

/** The outline's part that isn't on a slide: what a section is for, and whether the presenter has gone over it. */
export const SectionPlan = z
  .object({
    intent: z.string().max(200).optional(),
    reviewed: z.boolean().optional(),
  })
  .strict();
export type SectionPlan = z.infer<typeof SectionPlan>;

export const STUDIO_STEPS = ["basics", "outline", "slides", "review"] as const;
export const StudioStep = z.enum(STUDIO_STEPS);
export type StudioStep = z.infer<typeof StudioStep>;

/** One section of a suggested outline: a section's title, minutes and "what's ahead" lines, plus its intent. */
export const OutlineSection = z
  .object({
    id: z
      .string()
      .max(64)
      .regex(/^[a-z0-9][a-z0-9-]*$/)
      .optional(), // kept for sections that already exist
    title: z.string().min(1).max(80),
    minutes: z.number().int().min(1).max(120),
    points: z.array(z.string().max(60)).max(4).default([]),
    intent: z.string().max(200).default(""),
  })
  .strict();
export type OutlineSection = z.infer<typeof OutlineSection>;
export const Outline = z.object({ sections: z.array(OutlineSection).min(1).max(20) }).strict();
export type Outline = z.infer<typeof Outline>;

/** An empty slide standing in for a section's content until it's written: a concept titled "[To write]". */
export const PLACEHOLDER_TITLE = "[To write]";

export function isPlaceholderBlock(b: Block): boolean {
  return b.type === "concept" && b.title === PLACEHOLDER_TITLE && !b.bullets.length && !b.statement && !b.lead;
}

/** A section with nothing written yet: only placeholders. */
export function isEmptySection(s: Section): boolean {
  return s.blocks.every(isPlaceholderBlock);
}
